import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Coins, Check, Infinity as InfinityIcon, Sparkles, Repeat } from "lucide-react";
import { toast } from "sonner";

function Card({ item, kind, onBuy, highlight, currentPlan }) {
  const isCurrent = kind === "plan" && currentPlan === item.name.toLowerCase();
  return (
    <div data-testid={`${kind}-card-${item.lookup_key}`}
      className={`relative flex flex-col rounded-2xl border bg-card p-6 ${highlight ? "border-[#0B5D3B] shadow-[0_12px_32px_rgba(11,93,59,0.12)] dark:border-[#25D366]" : "border-border"}`}>
      {highlight && <span className="absolute -top-3 left-6 rounded-full bg-[#0B5D3B] px-3 py-1 text-xs font-semibold text-white dark:bg-[#25D366] dark:text-black">Most popular</span>}
      <h3 className="font-display text-lg font-semibold">{item.name}{kind === "plan" ? " plan" : " pack"}</h3>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="font-display text-4xl font-extrabold">${item.price}</span>
        <span className="text-sm text-muted-foreground">{kind === "plan" ? "/mo" : "USD"}</span>
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-xl bg-[#E8F5EE] px-3 py-2 text-sm font-semibold text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]">
        <Coins className="h-4 w-4 text-[#D97706]" /> {item.tokens} tokens{kind === "plan" ? "/mo" : ""}
        <span className="ml-auto flex items-center gap-1 text-xs font-normal">
          {kind === "plan" ? <><Repeat className="h-3.5 w-3.5" />renews</> : <><InfinityIcon className="h-3.5 w-3.5" />never expire</>}
        </span>
      </div>
      {item.perks && <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground"><Check className="h-4 w-4 text-[#059669]" />{item.perks}</p>}
      <div className="mt-6">
        <Button onClick={() => onBuy(item)} disabled={isCurrent} data-testid={`buy-${item.lookup_key}`}
          className="w-full bg-[#0B5D3B] text-white hover:bg-[#08472D]">
          {isCurrent ? "Current plan" : kind === "plan" ? "Subscribe" : "Buy now"}
        </Button>
      </div>
    </div>
  );
}

export default function Billing() {
  const [cat, setCat] = useState(null);
  const { balance } = useAuth();

  useEffect(() => { api.get("/billing/catalog").then(({ data }) => setCat(data)).catch(() => setCat({ packs: [], plans: [] })); }, []);

  const buy = async (item) => {
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key: item.lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!cat) return <div className="grid gap-5 sm:grid-cols-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}</div>;

  return (
    <div className="space-y-10" data-testid="billing-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Tokens &amp; plans</h1>
        <p className="mt-1 text-muted-foreground">Top up anytime or subscribe for the cheapest tokens. You have <strong className="text-foreground">{balance}</strong> now.{cat.plan ? ` Current plan: ${cat.plan}.` : ""}</p>
      </div>

      <section>
        <h2 className="mb-4 font-display text-xl font-semibold">Monthly plans <span className="text-sm font-normal text-muted-foreground">— best value</span></h2>
        <div className="grid gap-6 sm:grid-cols-3">
          {cat.plans.map((p) => <Card key={p.lookup_key} item={p} kind="plan" onBuy={buy} highlight={p.name === "Plus"} currentPlan={cat.plan} />)}
        </div>
      </section>

      <section>
        <h2 className="mb-4 font-display text-xl font-semibold">Token packs <span className="text-sm font-normal text-muted-foreground">— pay as you go, never expire</span></h2>
        <div className="grid gap-6 sm:grid-cols-3">
          {cat.packs.map((p) => <Card key={p.lookup_key} item={p} kind="pack" onBuy={buy} highlight={p.name === "Medium"} />)}
        </div>
      </section>

      <div className="flex items-start gap-3 rounded-2xl border border-border bg-accent/40 p-5 text-sm text-muted-foreground">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#0B5D3B] dark:text-[#25D366]" />
        Secure checkout by Stripe. Test card 4242 4242 4242 4242, any future expiry and CVC. A blueprint is 15 tokens, a first evaluation 20, re-evaluations 15, chat 2 each.
      </div>
    </div>
  );
}
